import { useCallback, useState, useMemo } from 'react';
import type { ActionMeta, MultiValue, FormatOptionLabelMeta, MultiValueGenericProps } from 'react-select';
import Select, { components } from 'react-select';
import styled from '@emotion/styled';
import { useQuery } from '@tanstack/react-query';
import type { BrandInsightsInstanceContext, TagFilter } from '@trinity/chat';
import { Chip } from '@trinity/chat-components/components/presentation';
import { useTheme } from '@trinity/chat-components/components/Theme';
import { useAppTheming } from '@trinity/chat-components/containers/AppTheming/index.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import * as Presentation from './Presentation';

type Props = {
  context: BrandInsightsInstanceContext.ItemWithMetadata;
  value: TagFilter.Item[];
  onChange: (value: TagFilter.Item[], meta: ActionMeta<TagFilter.Item>) => unknown;
};

export function Tag({ context, value, onChange }: Props) {
  const theme = useTheme();
  const theming = useAppTheming();

  const selectStyles = useMemo(() => Presentation.buildSelectStyles(theme), [theme]);
  const selectTheme = useMemo(() => Presentation.buildSelectTheme(theme), [theme]);

  const selected = useMemo(() => value, [value]);

  const handleChange = useCallback(
    (value: MultiValue<TagFilter.Item>, meta: ActionMeta<TagFilter.Item>) => {
      if (meta.action === 'clear') {
        const immutableTags = selected.filter((t) => t.metadata?.canRemove === false);
        onChange(immutableTags, meta);
      } else {
        onChange(Array.from(value), meta);
      }
    },
    [onChange, selected]
  );

  const [inputValue, setInputValue] = useState('');

  const apiClient = useChatApiClient();

  const optionsQuery = useQuery({
    queryKey: [`get:brand-insights/chat/filters/tags`, context, inputValue],
    queryFn: async () => {
      return apiClient.chat
        .searchTags({
          context,
          query: inputValue,
        })
        .then((r) => r.data.results);
    },
    placeholderData: [],
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    staleTime: 5000,
  });

  const handleInputChange = useCallback((newValue: string) => setInputValue(newValue), []);

  const handleNoOptionsMessage = useCallback(
    (obj: { inputValue: string }) => {
      if (optionsQuery.isFetching) return `Loading...`;
      return obj.inputValue?.length ? `No tags found` : null;
    },
    [optionsQuery.isFetching]
  );

  const formatOptionLabel = useCallback(
    (option: TagFilter.Item, metadata: FormatOptionLabelMeta<TagFilter.Item>) => {
      const isInDropdown = metadata.context === 'menu';

      // White label theming for Chip via CSS custom properties
      const chipStyles: React.CSSProperties | undefined =
        theming.enabled && theming.palette.primary
          ? ({
              '--chip-bg': isInDropdown ? 'transparent' : theming.palette.primary.lighter,
              '--chip-border': theming.palette.primary.main,
              '--chip-color': theming.palette.primary.main,
            } as React.CSSProperties)
          : undefined;

      return (
        <div style={{ display: 'flex', alignItems: 'center', gap: '6px', pointerEvents: 'none' }}>
          <span>{option.name}</span>
          {!!option.metadata?.segmentIdentifier && (
            <StyledChip selected={true} style={chipStyles}>
              Segment
            </StyledChip>
          )}
          {typeof option.metadata?.journeyId === 'number' && (
            <StyledChip selected={true} style={chipStyles}>
              Journey
            </StyledChip>
          )}
        </div>
      );
    },
    [theming]
  );

  return (
    <Presentation.Row>
      <Presentation.Label>Tag</Presentation.Label>
      <Presentation.Value>
        <Select<TagFilter.Item, true>
          styles={selectStyles}
          components={{
            DropdownIndicator: null,
            MultiValueRemove: MultiValueRemove,
          }}
          theme={selectTheme}
          value={selected}
          isMulti
          isSearchable
          isClearable
          isLoading={optionsQuery.isFetching}
          options={optionsQuery.data}
          placeholder={`Search tags...`}
          noOptionsMessage={handleNoOptionsMessage}
          formatOptionLabel={formatOptionLabel}
          getOptionLabel={(o) => o.name}
          getOptionValue={(o) => `${o.id}`}
          onChange={handleChange}
          onInputChange={handleInputChange}
          menuPlacement="auto"
        />
      </Presentation.Value>
    </Presentation.Row>
  );
}

const MultiValueRemove = (props: MultiValueGenericProps<TagFilter.Item, true>) => {
  if (props.data.metadata?.canRemove === false) {
    return null;
  }
  return <components.MultiValueRemove {...props} />;
};

const StyledChip = styled(Chip)({
  borderRadius: 12,
  padding: '2px 5px',
});
